/**
 * Script Compiler
 *
 * Three-stage pipeline for user-authored TypeScript scripts:
 * 1. Static analysis — reject forbidden constructs (regex-based)
 * 2. Type stripping — remove TypeScript syntax (regex-based, no esbuild dependency)
 * 3. Sandbox execution — wrap in `new Function()` with controlled scope
 *
 * Returns a ScriptFactory: call it to get a fresh closure per behavior instance
 * (each instance gets independent module-level state).
 *
 * ## Security model
 *
 * This is **defense-in-depth via regex validation**, not a true sandbox.
 * `validateSource` blocks known dangerous patterns (eval, DOM access, etc.)
 * and `new Function()` limits injected globals to Math, JSON, and network APIs
 * (fetch, XMLHttpRequest, WebSocket). However:
 *
 * - Indirect eval (`(0,eval)(...)`) bypasses the regex check.
 * - Prototype chain access (`({}).constructor.constructor(...)`) can reach Function.
 * - Runtime string construction can assemble forbidden identifiers.
 *
 * The static checks are effective against accidental misuse and casual abuse.
 * For untrusted third-party scripts, a Worker-based sandbox would be needed.
 */

import { Vec3Impl, MathUtilImpl } from './script-math';

// ── Types ────────────────────────────────────────────────

export interface CompiledScript {
    onStart?: (ctx: unknown) => void | Promise<void>;
    onUpdate?: (ctx: unknown) => void;
    onDestroy?: (ctx: unknown) => void;
    onSelect?: (ctx: unknown) => void | Promise<void>;
    onSelectExit?: (ctx: unknown) => void | Promise<void>;
    onHover?: (ctx: unknown) => void | Promise<void>;
    onHoverExit?: (ctx: unknown) => void | Promise<void>;
    onEvent?: (ctx: unknown, eventName: string) => void | Promise<void>;
}

/** Factory that creates a fresh script instance (own closure) per behavior */
export type ScriptFactory = () => CompiledScript;

export class ScriptCompileError extends Error {
    constructor(public readonly violations: string[]) {
        super(`Script compilation failed:\n${violations.join('\n')}`);
        this.name = 'ScriptCompileError';
    }
}

// ── Known hook names ─────────────────────────────────────

const HOOK_NAMES = [
    'onStart', 'onUpdate', 'onDestroy',
    'onSelect', 'onSelectExit',
    'onHover', 'onHoverExit',
    'onEvent',
] as const;

// ── Stage 1: Static Analysis ─────────────────────────────

/**
 * Forbidden patterns — checked against the raw source text.
 * Each entry is [regex, human-readable violation message].
 */
const FORBIDDEN_PATTERNS: [RegExp, string][] = [
    // Non-type imports (import type is OK)
    [/^\s*import\s+(?!type\b)/gm, 'Runtime import statements are forbidden (use `import type` for types)'],
    // require() calls
    [/\brequire\s*\(/g, '`require()` is forbidden'],
    // eval / Function constructor
    [/\beval\s*\(/g, '`eval()` is forbidden'],
    [/\bnew\s+Function\s*\(/g, '`new Function()` is forbidden'],
    // Timers
    [/\bsetTimeout\s*\(/g, '`setTimeout()` is forbidden'],
    [/\bsetInterval\s*\(/g, '`setInterval()` is forbidden'],
    [/\brequestAnimationFrame\s*\(/g, '`requestAnimationFrame()` is forbidden'],
    // Global access
    [/\bglobalThis\b/g, '`globalThis` is forbidden'],
    [/\bwindow\b/g, '`window` is forbidden'],
    [/\bdocument\b/g, '`document` is forbidden'],
    [/\bprocess\b/g, '`process` is forbidden'],
    // TypeScript constructs with runtime semantics
    [/^\s*export\s+enum\b/gm, '`enum` declarations are forbidden (use string unions or const objects)'],
    [/^\s*enum\b/gm, '`enum` declarations are forbidden (use string unions or const objects)'],
    [/^\s*namespace\b/gm, '`namespace` declarations are forbidden'],
    [/^\s*export\s+namespace\b/gm, '`namespace` declarations are forbidden'],
];

/**
 * Scan raw TypeScript source for forbidden constructs.
 * @returns Array of violation messages (empty = clean)
 */
export function validateSource(source: string): string[] {
    const violations: string[] = [];

    for (const [pattern, message] of FORBIDDEN_PATTERNS) {
        // Reset lastIndex for global regexes
        pattern.lastIndex = 0;
        if (pattern.test(source)) {
            violations.push(message);
        }
    }

    return violations;
}

// ── Stage 2: Type Stripping ──────────────────────────────

/**
 * Strip TypeScript type syntax from source code.
 * Handles the restricted subset allowed in scripts:
 * - `import type { ... } from '...'` → removed
 * - `export interface Foo { ... }` → removed
 * - `export type Foo = ...` → removed
 * - `: TypeAnnotation` on params, returns, variables → removed
 * - `as Type` assertions → removed
 *
 * This is a line-by-line + simple state machine approach.
 * It does NOT parse a full AST — it handles the patterns
 * that appear in our restricted script subset.
 */
export function stripTypes(source: string): string {
    const lines = source.split('\n');
    const result: string[] = [];
    let inInterfaceOrType = false;
    let braceDepth = 0;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        // Skip `import type` statements
        if (/^\s*import\s+type\s+/.test(line)) {
            continue;
        }

        // Handle multi-line interface/type removal
        if (inInterfaceOrType) {
            for (const ch of line) {
                if (ch === '{') braceDepth++;
                if (ch === '}') braceDepth--;
            }
            if (braceDepth <= 0) {
                inInterfaceOrType = false;
                braceDepth = 0;
            }
            continue;
        }

        // `export interface Foo { ... }` or `interface Foo { ... }`
        if (/^\s*(export\s+)?interface\s+/.test(trimmed)) {
            // Check if it's a single-line interface
            const openBraces = (line.match(/\{/g) || []).length;
            const closeBraces = (line.match(/\}/g) || []).length;
            if (openBraces > closeBraces) {
                inInterfaceOrType = true;
                braceDepth = openBraces - closeBraces;
            }
            // Either way, skip this line
            continue;
        }

        // `export type Foo = ...` or `type Foo = ...`
        if (/^\s*(export\s+)?type\s+\w+/.test(trimmed)) {
            // Single-line type alias — skip
            // Multi-line: check for unclosed braces
            const openBraces = (line.match(/\{/g) || []).length;
            const closeBraces = (line.match(/\}/g) || []).length;
            if (openBraces > closeBraces) {
                inInterfaceOrType = true;
                braceDepth = openBraces - closeBraces;
            }
            continue;
        }

        // Strip inline type annotations from the remaining code
        const stripped = stripInlineTypes(line);
        result.push(stripped);
    }

    return result.join('\n');
}

/**
 * Strip inline TypeScript annotations from a single line of code.
 * Handles: parameter types, return types, variable types, `as` casts, generics on calls.
 */
function stripInlineTypes(line: string): string {
    let result = line;

    // Remove `as Type` assertions (e.g., `value as number`, `x as Foo`)
    // Matches both uppercase and lowercase type names (number, string, Foo, etc.)
    result = result.replace(/\bas\s+[a-zA-Z][\w<>,\s|&[\]]*(?=[,;)\]\s}]|$)/g, '');

    // Remove generic type params on function calls: `foo<Type>(` → `foo(`
    // Only matches when followed by `(` to avoid matching comparison operators
    result = result.replace(/(\w+)\s*<[A-Z][\w<>,\s|&[\]]*>\s*\(/g, '$1(');

    // Remove return type annotations: `): Type {` → `) {`
    result = result.replace(/\)\s*:\s*[A-Z][\w<>,\s|&[\].]*(?=\s*\{|$)/g, ')');
    // Also handle Promise return types and other complex returns
    result = result.replace(/\)\s*:\s*(?:void|string|number|boolean|Promise<[^>]*>|[\w<>,\s|&[\].]+)(?=\s*\{|$)/g, ')');

    // Remove variable/const type annotations: `const x: Type =` → `const x =`
    // Handles simple types, generics, and inline object types like { x: number; y: number }
    result = result.replace(/((?:const|let|var)\s+\w+)\s*:\s*\{[^}]*\}\s*(?==)/g, '$1 ');
    result = result.replace(/((?:const|let|var)\s+\w+)\s*:\s*[\w<>,|&[\].']+\s*(?==)/g, '$1 ');

    // Remove parameter type annotations in function signatures
    // Only strip `: Type` when preceded by a parameter name inside parentheses
    result = stripParamTypes(result);

    return result;
}

/**
 * Strip type annotations from function parameters.
 * Handles: `(a: number, b: string)` → `(a, b)`
 * Also handles optional params: `(a?: number)` → `(a)`
 * And destructured params with types.
 */
function stripParamTypes(line: string): string {
    let result = line;

    // Only strip parameter types if the line looks like a function declaration
    // (contains 'function ' or '=>' or starts with '(')
    if (!/\bfunction\b|=>/.test(result) && !/^\s*\(/.test(result)) {
        return result;
    }

    // Simple parameter types: `paramName: Type` or `paramName?: Type`
    // Followed by `,` or `)` or `=`
    result = result.replace(/(\w+)\??\s*:\s*(?:Readonly<[^>]*>|[\w<>,\s|&[\].]+?)(?=\s*[,)=])/g, '$1');

    return result;
}

// ── Stage 3: Sandbox Execution ───────────────────────────

/**
 * Wrap stripped JavaScript in a sandboxed function and extract hook exports.
 */
function createSandboxedModule(jsSource: string): CompiledScript {
    // Rewrite export declarations to plain declarations
    let body = jsSource;
    body = body.replace(/^export\s+async\s+function\s+/gm, 'async function ');
    body = body.replace(/^export\s+function\s+/gm, 'function ');
    body = body.replace(/^export\s+const\s+/gm, 'const ');
    body = body.replace(/^export\s+let\s+/gm, 'let ');
    body = body.replace(/^export\s+var\s+/gm, 'var ');

    // Build the wrapped source that collects hooks
    const hookChecks = HOOK_NAMES.map(name =>
        `if (typeof ${name} === 'function') __exports.${name} = ${name};`
    ).join('\n');

    const wrappedSource = `
'use strict';
var __exports = {};
${body}
${hookChecks}
return __exports;
`;

    // Execute with controlled scope — no globals, no DOM
    // Injected: Math, JSON, network APIs, Vec3/MathUtil utilities
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    const factory = new Function('Math', 'JSON', 'fetch', 'XMLHttpRequest', 'WebSocket', 'Vec3', 'MathUtil', wrappedSource);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const exports = factory(Math, JSON, fetch, XMLHttpRequest, WebSocket, Vec3Impl, MathUtilImpl) as CompiledScript;
    return exports;
}

// ── Stage 4: Dry-Run Validation ──────────────────────────

/**
 * Create a deeply-proxied mock context for dry-running hooks.
 *
 * Property reads return safe defaults (0, '', false, nested proxies).
 * Method calls return safe defaults. The goal is to let typical script
 * code execute without crashing on context access, so that real errors
 * (undefined globals, typos, bad logic) surface instead.
 */
function createMockContext(): unknown {
    const handler: ProxyHandler<Record<string, unknown>> = {
        get(_target, prop) {
            // Symbol properties (used by JS internals)
            if (typeof prop === 'symbol') return undefined;

            // Numeric property names — return 0 (e.g., array-like access)
            if (!isNaN(Number(prop))) return 0;

            // Common context properties that return specific types
            if (prop === 'deltaTime' || prop === 'elapsed') return 0;
            if (prop === 'x' || prop === 'y' || prop === 'z' || prop === 'w') return 0;
            if (prop === 'name' || prop === 'path') return '';
            if (prop === 'active' || prop === 'isPlaying') return false;

            // Primitive coercion — return sensible defaults so expressions
            // like `ctx.data.speed * 2` produce 0 instead of NaN cascades
            if (prop === 'valueOf') return () => 0;
            if (prop === 'toString') return () => '';

            // Return a callable proxy for everything else.
            // This lets property reads chain (ctx.self.position.x → 0)
            // and method calls succeed (ctx.scene.find("X") → proxy).
            return new Proxy(function _mock() { /* no-op */ }, {
                apply() {
                    return createMockContext();
                },
                get(_fnTarget, fnProp) {
                    return handler.get!({}, fnProp, _fnTarget) as unknown;
                },
            });
        },
        set() {
            return true;
        },
    };

    return new Proxy({}, handler);
}

/**
 * Dry-run all hooks in a compiled script with a mock context.
 * Catches runtime errors that only manifest when hooks execute
 * (e.g., undefined globals, property access on wrong types).
 *
 * @returns Array of error messages (empty = all hooks passed)
 */
export function dryRunHooks(factory: ScriptFactory): string[] {
    const errors: string[] = [];

    let instance: CompiledScript;
    try {
        instance = factory();
    } catch (err) {
        errors.push(`Factory error: ${err instanceof Error ? err.message : String(err)}`);
        return errors;
    }

    const syncHooks = ['onStart', 'onUpdate', 'onDestroy', 'onSelect', 'onSelectExit', 'onHover', 'onHoverExit'] as const;
    for (const hookName of syncHooks) {
        const hook = instance[hookName];
        if (!hook) continue;
        try {
            void hook(createMockContext());
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            errors.push(`${hookName}: ${msg}`);
        }
    }

    // onEvent takes an extra eventName param
    if (instance.onEvent) {
        try {
            void instance.onEvent(createMockContext(), 'test');
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            errors.push(`onEvent: ${msg}`);
        }
    }

    return errors;
}

// ── Public API ───────────────────────────────────────────

/**
 * Compile a TypeScript script source into a factory function.
 * The factory creates fresh instances with independent module-level state.
 *
 * @param typeScriptSource - Raw TypeScript source code
 * @returns Factory function that creates CompiledScript instances
 * @throws ScriptCompileError if source contains forbidden constructs
 */
export function compileScriptFactory(typeScriptSource: string): ScriptFactory {
    // Stage 1: Static analysis
    const violations = validateSource(typeScriptSource);
    if (violations.length > 0) {
        throw new ScriptCompileError(violations);
    }

    // Stage 2: Strip types
    const jsSource = stripTypes(typeScriptSource);

    // Stage 3: Return a factory that creates fresh sandboxed instances
    // Each call produces a new closure with independent module-level state
    return () => createSandboxedModule(jsSource);
}
